import { ref, computed } from 'vue';

/**
 * Coach-mark onboarding tour for the levelling surfaces (ported from the prototype's
 * `LvlTour`).
 *
 * A spotlight ring over the target plus a card with Back / Next / Skip. Steps with no
 * target render centred, which is how a concept step avoids pointing at nothing.
 *
 * Runs itself ONCE per surface and then stays out of the way; the page-header button
 * replays it on demand. Completion lives in localStorage — a per-browser nicety, not
 * tenant config, and nothing downstream depends on it.
 *
 * Targets are addressed by `data-tour` attribute rather than by CSS class. The prototype
 * mixes both, and its class-based steps are the ones that silently stop matching when a
 * layout is refactored — a tour pointing at nothing is worse than no tour, because it
 * still dims the screen.
 */

/** Bumped when the steps change materially enough that a repeat is worth showing. */
const TOUR_REV = 1;

/**
 * The control-centre tour.
 *
 * Copy follows the prototype's intent but drops its demo-org references ("Northwind",
 * "on day one nothing is levelled yet") — this runs against a real register that may be
 * part-levelled, and telling someone nothing is levelled when half of it is reads as a
 * bug in the product rather than a tour.
 */
export const LEVELLING_CC_TOUR = [
  {
    target: 'pagehead',
    title: 'Your levelling headquarters',
    body: 'Every role in the register lives here with its status, method and level. This is'
      + ' where levelling work starts and where you track it.',
  },
  {
    target: 'stats',
    title: 'The pipeline at a glance',
    body: 'These counters follow the register through levelling — not started, in progress,'
      + ' awaiting review, levelled. Select one to filter the table to just those roles.',
  },
  {
    target: 'anchor',
    title: 'Anchors pin the framework',
    body: 'An anchor is an evaluated role everyone agrees on, used as the reference its'
      + ' family is compared against. This says how many still need evaluating, and opens'
      + ' the guided setup: the CEO ceiling first, then a family spine, then wider.',
  },
  {
    target: 'actions',
    title: 'Start here',
    body: 'Level a new role opens the evaluation journey — who levels it, and whether it is'
      + ' scored in full or classified against the ladder.',
    place: 'left',
  },
  {
    /* The whole table head — title, count and tools — not just the chip group on the
       right. The step is about the register as a working surface, and ringing only the
       controls made it read as "here are some buttons". */
    target: 'register',
    title: 'Work the register',
    body: 'Search, filter by status, isolate anchors, switch density and tailor the columns.'
      + ' Your view is saved for next time.',
  },
];

/**
 * @param {Object} opts
 * @param {Array}  opts.steps      step definitions
 * @param {string} opts.storageKey where completion is remembered
 */
/**
 * Levelling framework — the Levels and tracks tab (prototype tour `matrix`).
 *
 * Four steps, in the order someone meets the surface: what the tabs hold, the ladder
 * itself, the controls that reshape it, and where the vocabulary is explained.
 */
export const LEVELLING_FRAMEWORK_TOUR = [
  {
    target: 'fw-tabs',
    title: 'Your levelling framework',
    body: 'These tabs hold everything about your framework — the ladder itself, what each'
      + ' level means for every factor, and how it is governed.',
  },
  {
    target: 'fw-grid',
    title: 'Levels down, tracks across',
    body: 'Every role you level lands on one of these cells. Tracks separate operational,'
      + ' individual contributor, management and executive careers — a level means the'
      + ' same seniority whichever track it sits on.',
  },
  {
    target: 'fw-filter',
    title: 'Slice it your way',
    body: 'Switch View to Descriptions to read what each level actually means — worth a'
      + ' look before your first evaluation. Narrow the level range, pick a discipline to'
      + ' compare against, or switch tracks off to focus.',
  },
  {
    /* Sits at the far right of the header, so the card is right-aligned to it rather
       than running off the edge of the screen. */
    target: 'fw-terms',
    title: 'New words live here',
    body: 'Anchor, coverage, read-across — every levelling term used on these tabs is'
      + ' explained under Terms.',
    place: 'left',
  },
];

/**
 * Levelling framework — the Framework in use tab (prototype tour `org`).
 *
 * The prototype's second step ("Right now it is empty", "none of Northwind's 1,000
 * roles carry a level yet") only holds for its greenfield demo org, where that tour
 * auto-runs. Against a real register the grid is usually part-populated, so that step
 * is replaced by the thing people actually misread here: a hatched cell and an empty
 * cell mean different things, and neither is a gap.
 */
export const LEVELLING_FRAMEWORK_IN_USE_TOUR = [
  {
    target: 'fwiu-intro',
    title: 'Where your roles sit',
    body: 'This grid plots your actual register on the framework — which cells your'
      + ' organisation really uses, and which stay empty.',
  },
  {
    target: 'fwiu-grid',
    title: 'Empty cells are not gaps',
    body: 'A hatched cell is a level that does not exist on that track at all. An empty'
      + ' one exists but nobody sits there yet — also not a gap, because a level nobody'
      + ' occupies does not need an anchor. Only cells with roles in them are scored.',
  },
  {
    target: 'fwiu-show',
    title: 'Anchors pin families to the ladder',
    body: 'An anchor is an evaluated role everyone agrees on, used as the reference point'
      + ' for its family. Once anchors exist, switch Show to Coverage to score each'
      + ' populated cell against the anchor floor.',
  },
  {
    /* The panel sits on the right, so the card is right-aligned to it rather than
       running off the edge of the screen. */
    target: 'fwiu-panel',
    title: 'Select a cell to act on it',
    body: 'Selecting a cell lists the roles at that level and track, and lets you make an'
      + ' evaluated one the anchor. Levelling itself happens in the Levelling control'
      + ' centre — an anchor can only ever be set from a completed full evaluation.',
    place: 'left',
  },
];

export function levellingTour({ steps, storageKey }) {
  const all = Array.isArray(steps) ? steps : [];
  const key = `${storageKey}:v${TOUR_REV}`;

  const self = {
    open: ref(false),
    index: ref(0),
    /** Target rect, or null for a centred concept step. */
    rect: ref(null),
  };

  self.steps = all;
  self.count = all.length;
  self.step = computed(() => all[self.index.value] || null);
  self.isLast = computed(() => self.index.value === all.length - 1);

  function seen() {
    try {
      return !!localStorage.getItem(key);
    } catch (err) {
      /* Private mode: treat as already seen rather than replaying on every visit. */
      return true;
    }
  }

  self.markDone = () => {
    try {
      localStorage.setItem(key, '1');
    } catch (err) {
      /* Nothing to do — it simply will not be remembered. */
    }
  };

  /** True only on a first visit. The host calls this once the surface has rendered. */
  self.shouldAutoRun = () => all.length > 0 && !seen();

  /* ---- measuring ----------------------------------------------------------- */

  const elFor = (step) => (step && step.target
    ? document.querySelector(`[data-tour="${step.target}"]`)
    : null);

  /**
   * Bring the target into view WITHOUT scrollIntoView.
   *
   * The control centre is a viewport-bound workspace: the page itself does not scroll,
   * only the table body does. `scrollIntoView` walks up to the document and would either
   * do nothing or jerk the whole shell. Adjusting the nearest scrollable ancestor so the
   * target sits in its middle third is what actually works here.
   */
  function reveal(el) {
    let p = el.parentElement;
    while (p && p !== document.body) {
      const st = getComputedStyle(p);
      if (/(auto|scroll)/.test(st.overflowY) && p.scrollHeight > p.clientHeight + 4) break;
      p = p.parentElement;
    }
    if (!p || p === document.body) return;
    const pr = p.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    const above = er.top - pr.top;
    const below = er.bottom - pr.bottom;
    if (above < 70) p.scrollTop += above - 70;
    else if (below > -40) p.scrollTop += Math.min(below + 40, above - 70);
  }

  /** Re-read the target's position. Call after a step change, a resize or a scroll. */
  self.measure = () => {
    /* A closed tour must not touch the page. The host listens for every scroll on the
       page, and `step` still names step 0 while closed — so without this, each scroll
       ran reveal() on that target and snapped its scroller back to it. On Framework in
       Use that target is the intro at the top, which made the view unscrollable. */
    if (!self.open.value) return;
    const step = self.step.value;
    if (!step) return;
    const el = elFor(step);
    if (!el) {
      /* No target, or the target is not on this screen: render centred rather than
         ringing the top-left corner. */
      self.rect.value = null;
      return;
    }
    reveal(el);
    /* Measured AFTER the scroll settles, not during it — otherwise the ring lands where
       the target used to be. */
    requestAnimationFrame(() => {
      const again = elFor(step);
      const r = again ? again.getBoundingClientRect() : null;
      self.rect.value = r
        ? { left: r.left, top: r.top, width: r.width, height: r.height }
        : null;
    });
  };

  /* ---- geometry ------------------------------------------------------------ */

  const PAD = 6;
  const CARD_W = 330;
  const GAP = 12;

  self.ringStyle = computed(() => {
    const r = self.rect.value;
    if (!r) return null;
    return {
      left: `${r.left - PAD}px`,
      top: `${r.top - PAD}px`,
      width: `${r.width + PAD * 2}px`,
      height: `${r.height + PAD * 2}px`,
    };
  });

  /**
   * Card placement: below the ring, above it when there is no room, and right-aligned to
   * the target for `place: 'left'` — used where the target sits at the far right of the
   * header and a left-aligned card would run off-screen. Clamped to the viewport either
   * way.
   */
  self.cardStyle = computed(() => {
    const r = self.rect.value;
    const step = self.step.value;
    if (!r) {
      return { left: '50%', top: '38%', transform: 'translate(-50%,-50%)', width: `${CARD_W}px` };
    }
    const ringLeft = r.left - PAD;
    const ringTop = r.top - PAD;
    const ringW = r.width + PAD * 2;
    const ringH = r.height + PAD * 2;
    const below = ringTop + ringH + GAP;
    const fitsBelow = below + 190 < window.innerHeight;
    const top = fitsBelow ? below : Math.max(16, ringTop - GAP - 180);
    let left = step && step.place === 'left' ? ringLeft + ringW - CARD_W : ringLeft;
    left = Math.min(Math.max(12, left), window.innerWidth - CARD_W - 12);
    return { left: `${left}px`, top: `${top}px`, width: `${CARD_W}px` };
  });

  /* ---- transitions --------------------------------------------------------- */

  self.start = () => {
    if (!all.length) return;
    self.index.value = 0;
    self.open.value = true;
    self.measure();
  };

  self.next = () => {
    if (self.isLast.value) {
      self.close();
      return;
    }
    self.index.value += 1;
    self.measure();
  };

  self.back = () => {
    if (self.index.value === 0) return;
    self.index.value -= 1;
    self.measure();
  };

  /**
   * Close, and remember it — including on Skip.
   *
   * Skipping counts as done deliberately: someone who dismissed the tour does not want it
   * again on every visit, and the header button is there when they do.
   */
  self.close = () => {
    self.open.value = false;
    self.markDone();
  };

  return self;
}
